"use client";
import NextImage from "next/image";
import NextLink from "next/link";
import { Button as ReactAriaButton } from "react-aria-components";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Avatar } from "@jetblue/kit/avatar";
import { Popover } from "@jetblue/kit/popover";
import { Ad, type AdProperties } from "../ad";
import {
  AuthenticatedCallToActions,
  type AuthenticatedCallToActionsProperties,
} from "./call-to-actions";
import {
  CreditProfileUser,
  type CreditProfileUserProperties,
} from "./credit-profile-user";
import { TrueBlueUser, type TrueBlueUserProperties } from "./true-blue-user";
import type { EnhancedUserData, LogoutOptions } from "@jetblue/dotcom-auth";
import { constructReturnURL } from "../construct-return-url";

export type AuthenticatedProperties = {
  creditProfileMenu: {
    ad: AdProperties;
    cta: CreditProfileUserProperties["cta"];
    callToActions: AuthenticatedCallToActionsProperties;
    labels: CreditProfileUserProperties["labels"];
  };
  customerDetails: EnhancedUserData;
  handleLogout?: (options?: LogoutOptions) => Promise<void>;
  loggedInMenu: {
    ad: AdProperties;
    callToActions: AuthenticatedCallToActionsProperties;
    image?: string;
    imageAlt?: string;
    imageSrc?: unknown;
    loyalty: TrueBlueUserProperties["loyalty"];
    signOut: { href?: string; label?: string; target?: "_blank" | "_self" };
  };
};
export const Authenticated = ({
  creditProfileMenu,
  customerDetails,
  loggedInMenu,
  handleLogout,
}: AuthenticatedProperties) => {
  const {
    firstName,
    fullName,
    initials,
    isMosaic,
    userImageSrc,
    isCreditProfile,
  } = customerDetails;

  const { ad: creditProfileAd, callToActions: creditProfileCtas } =
    creditProfileMenu;
  const {
    ad: loyaltyAd,
    callToActions: loyaltyCtas,
    loyalty,
    signOut,
  } = loggedInMenu;

  const adData = isCreditProfile ? creditProfileAd : loyaltyAd;
  const callToActionData = isCreditProfile ? creditProfileCtas : loyaltyCtas;

  const signoutHref = signOut.href ?? "/logout";
  const pathname = usePathname();
  const [href, setHref] = useState(constructReturnURL(signoutHref));

  useEffect(() => {
    setHref(constructReturnURL(signoutHref));
  }, [signoutHref, pathname]);

  return (
    <Popover
      className="
        size-full overflow-scroll rounded-none border-none
        bg-background-surface-container p-0

        md:h-unset md:w-[375px] md:rounded-b-md md:shadow-jb-2
      "
      offset={0}
      placement="bottom right"
      showOverlayArrow={false}
      triggerButton={
        <ReactAriaButton
          aria-haspopup="dialog"
          className="
            relative flex h-full shrink-0 grow-0 cursor-pointer items-center
            justify-center gap-2

            after:absolute after:bottom-0 after:h-1 after:w-0
            after:bg-background-accent after:transition-all after:content-['_']

            hover:after:w-full

            focus-visible:outline-0

            focus-visible:after:absolute focus-visible:after:bottom-0
            focus-visible:after:h-1 focus-visible:after:w-0
            focus-visible:after:bg-background-accent
            focus-visible:after:transition-all focus-visible:after:content-['_']

            aria-expanded:after:w-full
          "
          data-fs-element={
            isCreditProfile
              ? "main-nav-menu-button-user-credit-profile"
              : "main-nav-menu-button-user-trueblue"
          }
        >
          <Avatar
            as={NextImage}
            initials={initials}
            isMosaic={isMosaic}
            name={fullName}
            size="small"
            source={userImageSrc}
          />

          {!isCreditProfile && (
            <span
              className="
                hidden text-label font-extrabold text-foreground-primary-variant

                lg:inline-block

                [html[data-header-bg-white=false]_&]:text-foreground-on-primary
              "
            >
              {customerDetails.loyaltyDetails?.accountBalance?.toLocaleString(
                "en",
              ) ?? 0}
              &nbsp;{loyalty.labels.pts}
            </span>
          )}
        </ReactAriaButton>
      }
    >
      {isCreditProfile ? (
        <CreditProfileUser
          cta={creditProfileMenu.cta}
          firstName={firstName}
          labels={creditProfileMenu.labels}
          loyalty={loyalty}
          travelBankDetails={customerDetails.travelBankDetails}
        />
      ) : (
        <TrueBlueUser
          customerDetails={customerDetails}
          isMosaic={isMosaic}
          loyalty={loyalty}
        />
      )}

      <AuthenticatedCallToActions
        {...callToActionData}
        dataAttributePrefix={
          isCreditProfile ? "user-credit-profile" : "user-trueblue"
        }
      />

      {(href || handleLogout) && signOut.label && (
        <section className="px-6 py-4">
          <NextLink
            className="
              block text-foreground-primary

              hover:text-foreground-primary-variant hover:underline

              focus-visible:focus-outline
            "
            data-fs-element={
              isCreditProfile
                ? "user-credit-profile-sign-out-link"
                : "user-trueblue-sign-out-link"
            }
            href={href}
            onClick={(event) => {
              if (!handleLogout) {
                return;
              }

              event.preventDefault();
              void handleLogout();
            }}
            target={signOut.target}
          >
            {signOut.label}
          </NextLink>
        </section>
      )}

      <Ad
        {...adData}
        dataAttributePrefix={
          isCreditProfile ? "user-credit-profile" : "user-trueblue"
        }
      />
    </Popover>
  );
};
